import {
  FormControl,
  InputLabel,
  useTheme,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useCallback, useState, useRef, useEffect } from "react";
import { DeleteForever, RestoreFromTrash } from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import {
  deleteChannel,
  loadChannels,
  restoreChannel,
} from "../../../pages/app/utils/dataLoader";
import { useChannels } from "../../../store/channels/hooks";

export const EditorDAWChannelSelector = ({
  channel,
  clip,
  daw,
  updateChannel,
  RULER_WIDTH,
  SELECT_WIDTH,
}) => {
  const theme = useTheme();
  const { channelList, setChannelList } = useChannels();
  const selectRef = useRef(null);
  const [selectOpen, setSelectOpen] = useState(false);

  const loadChannelData = useCallback(async () => {
    try {
      const channels = await loadChannels(clip.caseId, clip.id);
      setChannelList(channels);
    } catch (error) {
      console.error("Failed to load channel data:", error);
    }
  }, [clip.caseId, clip.id, setChannelList]);

  const handleDeleteChannel = useCallback(
    async (id) => {
      console.log("Deleting channel with ID:", id);
      await deleteChannel(clip.caseId, clip.id, id);
      await loadChannelData();
    },
    [clip.caseId, clip.id, loadChannelData],
  );

  const handleRestoreChannel = useCallback(
    async (id) => {
      console.log("Restoring channel with ID:", id);
      await restoreChannel(clip.caseId, clip.id, id);
      await loadChannelData();
    },
    [clip.caseId, clip.id, loadChannelData],
  );

  const handleToggle = (e, channel) => {
    e.stopPropagation();
    if (channel.status === "ACTIVE") {
      handleDeleteChannel(channel.id);
    } else {
      handleRestoreChannel(channel.id);
    }
  };

  useEffect(() => {
    loadChannelData();
  }, [loadChannelData]);

  return (
    <FormControl
      size="small"
      sx={{
        background: theme.palette.background.paper,
        borderRadius: 1,
        boxShadow: 1,
        left: 22,
        ml: `${RULER_WIDTH}px`,
        position: "absolute",
        top: 216,
        width: SELECT_WIDTH,
        zIndex: 10,
      }}
    >
      <InputLabel id="channel-select-label" sx={{ display: visuallyHidden }}>
        Channel Select
      </InputLabel>
      <Select
        ref={selectRef}
        displayEmpty
        labelId="channel-select-label"
        value={channelList.length ? channel : ""}
        disabled={!daw || !channelList.length}
        MenuProps={{
          disableRestoreFocus: true,
          "aria-hidden": !selectOpen,
        }}
        onChange={(event) => updateChannel(event.target.value)}
        onClose={() => setSelectOpen(false)}
        onOpen={() => setSelectOpen(true)}
        sx={{
          width: SELECT_WIDTH,
          "& .MuiSelect-select .MuiIconButton-root": {
            display: "none",
          },
        }}
      >
        {channelList.map((c) => (
          <MenuItem
            key={c.id}
            // value={index + 1}
            value={channelList.indexOf(c) + 1}
            // disabled={c.status !== "ACTIVE"}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: SELECT_WIDTH,
            }}
          >
            {/* Channel {index + 1} (of {channelList.length})
             */}
            <Typography
              onClick={(e) => c.status !== "ACTIVE" && e.stopPropagation()}
              sx={{
                opacity: c.status !== "ACTIVE" ? 0.5 : 1,
                textDecoration: c.status === "DELETED" && "line-through",
                width: 1,
              }}
            >
              Channel {channelList.indexOf(c) + 1} (of {channelList.length})
            </Typography>
            {c.status !== "DELETED" ? (
              <Tooltip
                title={
                  c.status === "ACTIVE" ? "Delete Channel" : "Restore Channel"
                }
              >
                <IconButton size="small" onClick={(e) => handleToggle(e, c)}>
                  {c.status === "ACTIVE" ? (
                    <DeleteForever color="error" fontSize="small" />
                  ) : (
                    <RestoreFromTrash color="secondary" fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            ) : null}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
